import { RenderFormat, ViewerDocumentKind, type ContentViewerSession } from '@/content-viewer/store/schemas'
import { createViewerSession } from '@/content-viewer/utils/create-viewer-session'
import type { UserFileViewerContext } from './build-user-file-viewer-session'

type BuildDeletedUserFileViewerSessionArgs = {
  userFileId: string
  title: string
  context: UserFileViewerContext
}

/**
 * Session for a source whose user-file record no longer resolves.
 *
 * Callers reach here when the active-files batch endpoint omits the id, which means the file was
 * deleted *or* is no longer reachable by this member — that endpoint is scoped to active files
 * within accepted matter membership, and nothing in its response separates the two. A failed
 * request throws instead of returning an empty result, so this is never the transient-failure path.
 *
 * The viewer still opens, showing the file's name and rendering the deleted state in the pane body,
 * because that is where the design puts the message. Refusing to open instead pushes the message
 * back to the caller, which is how it ended up squeezed into an events-table cell.
 *
 * No `downloadTarget`: there is nothing to download, and offering an action that cannot succeed is
 * the same dead end as the Retry button this replaces. The format is PDF because V1 living assets
 * only extract events from PDFs, and the pane resolves the deleted state from the API rather than
 * from the render format.
 *
 * That last part depends on a backend invariant: the batch endpoint that omitted the id and the
 * `viewable-url` endpoint the pane then calls are both scoped by
 * `UserFileRepository.get_member_active_files_by_ids`, so a record absent from the first is a 404 on
 * the second, which `useUserFileViewableContent` classifies as deleted. If those scopings ever
 * diverge, the pane would fall through to its generic retryable failure instead — the dead end this
 * replaces — so keep them aligned or carry the deleted fact on the session itself.
 */
export function buildDeletedUserFileViewerSession(args: BuildDeletedUserFileViewerSessionArgs): ContentViewerSession {
  const { userFileId, title, context } = args

  return createViewerSession({
    document: {
      kind: ViewerDocumentKind.USER_FILE,
      userFileId,
      title,
      renderAsset: { format: RenderFormat.PDF },
    },
    context,
  })
}
